// deviceSync — filling the offline cache the rest of Phase 4 reads.
// Spec: docs/specs/device-and-worker-sign-in.md §5, §4.4b. Phase 4f.
//
// ⚠️⚠️ THIS FILE CLOSES A GAP THAT MADE PHASES 4a–4d UNREACHABLE.
//
// `deviceOfflineCache` could store a roster, `deviceOfflineSignIn` could verify
// against one, and `deviceCaptureQueue` could sign captures for one — and
// NOTHING EVER WROTE ONE. Zero call sites for `device_roster()`,
// `device_profile()` or `device_face_templates()`. Every offline path would
// have found an empty cache and refused, correctly and uselessly, and the
// refusal ("this device has not connected yet") reads like a configuration
// problem rather than missing code. Four phases of tested machinery, unreachable.
//
// ⚠️ THE DEVICE SESSION MUST NOT TOUCH THE SHARED CLIENT. `supabase.auth
// .setSession()` would replace whatever worker is signed in on this tablet with
// the DEVICE account — an account deliberately built to read almost nothing —
// and every open query would start coming back empty. So the device session
// goes into an ISOLATED client (`createIsolatedClient`), which bakes the bearer
// into a header and persists nothing. The worker's session is untouched.

import { createIsolatedClient } from '@/lib/supabase'
import { getDeviceToken } from '@/lib/deviceSession'
import {
  saveOfflineRoster, stampServerContact, clearOfflineCache,
  DEFAULT_OFFLINE_MAX_SECONDS,
  type CachedWorker, type CachedTemplate, type CachedPick,
} from '@/lib/deviceOfflineCache'
import { withReadTimeout } from '@/lib/saveTimeout'
import { decodeJwtPayload } from '@/lib/offlineSession'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const ANON_KEY     = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const SESSION_URL  = `${SUPABASE_URL}/functions/v1/device-sign-in/session`

const PROFILE_KEY = 'kf49.device.profile'

export interface DeviceProfile {
  device_id:           string
  device_name:         string
  profile_key:         string
  label:               string
  permissions:         Record<string, string[]>
  money_ceiling:       string
  max_idle_seconds:    number
  allow_offline:       boolean
  offline_max_seconds: number
}

/**
 * The last-known profile, or null.
 *
 * ⚠️ Stored in PLAINTEXT, unlike the roster, and the difference is deliberate.
 * The roster is people — names, photos, face templates — and is encrypted
 * because a stolen tablet must not yield them. The profile is this device's own
 * configuration: a label, a permission map, two timeouts. Encrypting it would
 * buy nothing and would mean the screen could not read its own idle timeout
 * without the secret.
 *
 * ⚠️ It is NOT a permission source. The ceiling that matters is `has_perm()`
 * server-side (mig 20260818140000); this copy exists so an OFFLINE screen knows
 * how long it may stay unlocked and whether it may work offline at all. A
 * tablet editing this file gains nothing — every write is still re-judged.
 */
export function getCachedProfile(): DeviceProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY)
    return raw ? (JSON.parse(raw) as DeviceProfile) : null
  } catch {
    return null
  }
}

function saveProfile(p: DeviceProfile): void {
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)) } catch { /* best effort */ }
}

export function clearCachedProfile(): void {
  try { localStorage.removeItem(PROFILE_KEY) } catch { /* best effort */ }
}

/**
 * The offline window this device should use RIGHT NOW.
 *
 * ⚠️ Falls back to 0, not to the 24h default. No profile means we do not know
 * whether this device is allowed offline at all, and the safe reading of "we do
 * not know" is "not yet" — a device that has never synced has no roster to work
 * from anyway, so a permissive fallback would only produce a confusing refusal
 * further down.
 */
export function effectiveOfflineSeconds(p: DeviceProfile | null = getCachedProfile()): number {
  if (!p || !p.allow_offline) return 0
  return Math.min(Math.max(0, p.offline_max_seconds || 0), DEFAULT_OFFLINE_MAX_SECONDS)
}

/**
 * The tenant this device belongs to, read from its own session claim.
 *
 * The edge function stamps `app_metadata: { acct, device_id, tenant_id }` before
 * minting, so this is the same value `device_session_tenant()` resolves
 * server-side — which is what makes the badge hashes line up.
 */
function deviceTenantFromToken(token: string): string | null {
  const meta = decodeJwtPayload(token)?.app_metadata
  if (!meta || typeof meta !== 'object') return null
  const t = (meta as Record<string, unknown>).tenant_id
  return typeof t === 'string' && t.length > 0 ? t : null
}

export type SyncOutcome =
  | { ok: true;  profile: DeviceProfile; workers: number; templates: number; picks: number }
  | { ok: false; error: string; offline?: boolean; deviceRejected?: boolean }

type TokenResult =
  | { ok: true; token: string }
  | { ok: false; error: string; offline?: boolean; deviceRejected?: boolean }

/**
 * Turn a non-2xx /session response into the right kind of refusal.
 *
 * Split out because the THREE outcomes are genuinely different actions for
 * whoever is holding the tablet, and collapsing them into one "could not start"
 * is how a deploy fault gets read as a broken device.
 */
function sessionFailure(status: number, body: Record<string, unknown>): TokenResult {
  if (status === 401) {
    return { ok: false, error: 'This device is no longer allowed.', deviceRejected: true }
  }
  // 404 = the edge function is not deployed on this project. A site problem,
  // and saying so stops somebody re-scanning a badge at a server that cannot
  // answer.
  if (status === 404 || status >= 500) {
    return {
      ok: false,
      error: 'Sign-in is not switched on for this site yet. Ask your manager to set it up.',
    }
  }
  const msg = typeof body.error === 'string' ? body.error : 'Could not start this device.'
  return { ok: false, error: msg }
}

/** Exchange the bearer token for a short-lived device session. */
async function deviceAccessToken(): Promise<TokenResult> {
  const bearer = getDeviceToken()
  if (!bearer) return { ok: false, error: 'This device is not set up yet.', deviceRejected: true }

  let resp: Response
  try {
    resp = await fetch(SESSION_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: ANON_KEY,
        authorization: `Bearer ${bearer}`,
      },
      body: '{}',
    })
  } catch {
    return { ok: false, error: 'Could not reach the server.', offline: true }
  }

  const body = (await resp.json().catch(() => ({}))) as Record<string, unknown>
  const token = body.access_token
  if (!resp.ok || body.ok !== true || typeof token !== 'string' || !token) {
    return sessionFailure(resp.status, body)
  }
  return { ok: true, token }
}

/**
 * Refresh everything this device needs to work without a signal.
 *
 * Order matters at the end: the cache is written BEFORE contact is stamped. The
 * stamp is what re-opens the offline window, so stamping first would leave a
 * device believing it had a fresh roster it had failed to store — the one state
 * where the gate says yes and the cache says nothing.
 */
export async function syncDeviceCache(): Promise<SyncOutcome> {
  const session = await deviceAccessToken()
  if (!session.ok) return session

  const db = createIsolatedClient(session.token)

  // ⚠️ withReadTimeout, not withTimeout. These are reference READS — a failure
  // here must report itself as a query error, not as save telemetry.
  const [profileRes, rosterRes, templateRes, picksRes] = await Promise.all([
    withReadTimeout(db.rpc('device_profile'), 'device_profile'),
    withReadTimeout(db.rpc('device_roster'), 'device_roster'),
    withReadTimeout(db.rpc('device_face_templates'), 'device_face_templates'),
    withReadTimeout(db.rpc('device_open_picks'), 'device_open_picks'),
  ])

  if (profileRes.error || rosterRes.error) {
    return { ok: false, error: 'Could not load this device’s setup. Try again.' }
  }

  // device_profile() RETURNS TABLE … LIMIT 1, so PostgREST hands back an array.
  const profile = (profileRes.data as DeviceProfile[] | null)?.[0]
  if (!profile) {
    // The device row or its profile has gone. Not recoverable from the tablet.
    return { ok: false, error: 'This device needs to be set up again.', deviceRejected: true }
  }

  const workers = (rosterRes.data ?? []) as CachedWorker[]

  // ⚠️⚠️ THE TENANT ID IS THE BADGE-HASH SALT, NOT A LABEL. `device_roster()`
  // stores `sha256(tenant_id || ':' || badge_code)`, and `findWorkerByBadge`
  // re-derives it from `roster.tenant_id`. Put anything else in that field —
  // the device_id, a placeholder — and every hash misses, so every badge scans
  // as UNKNOWN_BADGE. That failure is silent and total: the roster looks fully
  // populated and nobody can sign in.
  //
  // It comes from the device session's own claim rather than being guessed,
  // because it must be byte-identical to what SQL used.
  const tenantId = deviceTenantFromToken(session.token)
  if (!tenantId) {
    // Without the salt a stored roster could never match a badge, so storing
    // one would be worse than storing none — it would look like a working
    // cache that refuses everybody.
    clearOfflineCache()
    saveProfile(profile)
    return { ok: false, error: 'This device needs to be set up again.', deviceRejected: true }
  }

  // ⚠️ Templates are allowed to fail on their own. They are POPIA special
  // personal information behind a separate function and a separate migration,
  // and a site that has not applied it yet should still get badge-and-PIN
  // offline rather than nothing. An empty template list degrades to
  // NO_TEMPLATE, which sends the worker to their PIN — a real path, not a
  // dead end.
  const templates = templateRes.error ? [] : ((templateRes.data ?? []) as CachedTemplate[])

  // ⚠️ Allowed to fail on its own, for the same reason templates are: it ships
  // in a later migration (20260828000000), and a site that has not applied it
  // should still get offline SIGN-IN. What it loses is the ability to start a
  // job, and the capture screen says so plainly rather than counting against
  // nothing.
  const picks = picksRes.error ? [] : ((picksRes.data ?? []) as CachedPick[])

  saveProfile(profile)

  if (profile.allow_offline) {
    const stored = await saveOfflineRoster({
      tenant_id: tenantId,
      fetched_at: Date.now(),
      workers,
      templates,
      picks,
    })
    if (!stored) {
      // No secret, or storage refused. The device works online; it must not
      // believe it can work offline.
      clearOfflineCache()
    }
  } else {
    // ⚠️ A profile that has been changed to forbid offline must DROP what it
    // already holds. Leaving a roster behind would keep names, photos and face
    // templates on a tablet that is no longer allowed to use them, until its
    // TTL happened to expire.
    clearOfflineCache()
  }

  stampServerContact()

  return { ok: true, profile, workers: workers.length,
           templates: templates.length, picks: picks.length }
}
